import { useState, useEffect } from 'react';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  Ticket, 
  Calendar,
  DollarSign,
  Users,
  Eye,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  UserCheck,
  Repeat,
  ArrowLeft,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const COLORS = ['#7c3aed', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4'];

const CURRENCY_CONFIG = {
  'NGN': { locale: 'en-NG', code: 'NGN', symbol: '₦' },
  'USD': { locale: 'en-US', code: 'USD', symbol: '$' },
  'CAD': { locale: 'en-CA', code: 'CAD', symbol: 'CA$' },
  'GBP': { locale: 'en-GB', code: 'GBP', symbol: '£' },
  'EUR': { locale: 'de-DE', code: 'EUR', symbol: '€' },
  'GHS': { locale: 'en-GH', code: 'GHS', symbol: 'GH₵' },
  'KES': { locale: 'en-KE', code: 'KES', symbol: 'KSh' },
  'ZAR': { locale: 'en-ZA', code: 'ZAR', symbol: 'R' },
};

function formatCurrency(amount, currencyCode = 'NGN') {
  const cfg = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG['NGN'];
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency',
      currency: cfg.code,
      minimumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${cfg.symbol}${Number(amount).toLocaleString()}`;
  }
}

function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-500/10 text-green-500',
    blue: 'bg-blue-500/10 text-blue-500',
    amber: 'bg-amber-500/10 text-amber-500',
    purple: 'bg-purple-500/10 text-purple-500',
  };

  return (
    <Card className="bg-card/50 border-border/50">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {trendValue && (
          <div className={`flex items-center gap-1 mt-3 text-xs ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trendValue}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function OrganizerAnalytics() {
  const { user, isOrganizer, isAdmin, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [eventAnalytics, setEventAnalytics] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [ticketSalesByType, setTicketSalesByType] = useState([]);
  const [audienceData, setAudienceData] = useState(null);
  const [viewsData, setViewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');
  const [myEvents, setMyEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(searchParams.get('event') || 'all');

  useEffect(() => {
    if (isOrganizer || isAdmin) {
      fetchMyEvents();
    }
  }, [isOrganizer, isAdmin]);

  useEffect(() => {
    if (isOrganizer || isAdmin) {
      fetchData();
    }
  }, [isOrganizer, isAdmin, selectedEvent]);

  // Auto-refresh analytics every 30 seconds for near real-time updates
  useEffect(() => {
    if (!isOrganizer && !isAdmin) return;
    const interval = setInterval(() => {
      fetchData();
      fetchTimeSensitiveData();
    }, 30000);
    return () => clearInterval(interval);
  }, [isOrganizer, isAdmin, selectedEvent, timeRange]);

  useEffect(() => {
    if (isOrganizer || isAdmin) {
      fetchTimeSensitiveData();
    }
  }, [timeRange, isOrganizer, isAdmin, selectedEvent]);

  // ROLE CHECK: Only organizers and admins can access analytics
  if (!authLoading && user && !isOrganizer && !isAdmin) {
    return <Navigate to="/my-tickets" replace />;
  }

  const eventParam = selectedEvent !== 'all' ? selectedEvent : null;

  const fetchMyEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/events/organizer/my-events`);
      setMyEvents(res.data);
    } catch (error) {
      console.error('Error fetching events list:', error);
    }
  };

  const handleEventFilter = (value) => {
    setSelectedEvent(value);
    if (value === 'all') {
      searchParams.delete('event');
    } else {
      searchParams.set('event', value);
    }
    setSearchParams(searchParams);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const eventQuery = eventParam ? `&event_id=${eventParam}` : '';
      const [overviewRes, eventsRes, ticketTypesRes, audienceRes] = await Promise.all([
        axios.get(`${API_URL}/api/organizer/analytics/overview${eventParam ? `?event_id=${eventParam}` : ''}`),
        axios.get(`${API_URL}/api/organizer/analytics/events${eventParam ? `?event_id=${eventParam}` : ''}`),
        axios.get(`${API_URL}/api/organizer/analytics/ticket-sales-by-type${eventParam ? `?event_id=${eventParam}` : ''}`),
        axios.get(`${API_URL}/api/organizer/analytics/audience${eventParam ? `?event_id=${eventParam}` : ''}`)
      ]);
      setOverview(overviewRes.data);
      setEventAnalytics(eventsRes.data);
      setTicketSalesByType(ticketTypesRes.data);
      setAudienceData(audienceRes.data);
      await fetchTimeSensitiveData();
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSensitiveData = async () => {
    try {
      const eventQuery = eventParam ? `&event_id=${eventParam}` : '';
      const [revenueRes, viewsRes] = await Promise.all([
        axios.get(`${API_URL}/api/organizer/analytics/revenue-trend?days=${timeRange}${eventQuery}`),
        axios.get(`${API_URL}/api/organizer/analytics/views?days=${timeRange}${eventQuery}`)
      ]);
      setRevenueTrend(revenueRes.data);
      setViewsData(viewsRes.data);
    } catch (error) {
      console.error('Error fetching time-sensitive data:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8" data-testid="organizer-analytics-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="p-3 rounded-xl bg-primary/10">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                {selectedEvent !== 'all'
                  ? `Viewing: ${myEvents.find(e => e.event_id === selectedEvent)?.title || 'Event'}`
                  : 'Track your event performance and revenue'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedEvent} onValueChange={handleEventFilter}>
              <SelectTrigger className="w-[200px]" data-testid="event-filter-select">
                <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {myEvents.map((event) => (
                  <SelectItem key={event.event_id} value={event.event_id}>
                    {event.title.length > 25 ? event.title.slice(0, 25) + '...' : event.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[140px]" data-testid="time-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">All time</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card/50">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="audience" data-testid="tab-audience">Audience</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                title="Total Events"
                value={overview?.events?.total || 0}
                subtitle={`${overview?.events?.published || 0} published, ${overview?.events?.upcoming || 0} upcoming`}
                icon={Calendar}
                color="primary"
              />
              <StatCard
                title="Tickets Sold"
                value={overview?.tickets?.total_sold || 0}
                subtitle="All time"
                icon={Ticket}
                color="blue"
              />
              <StatCard
                title="Total Revenue"
                value={formatCurrency(overview?.tickets?.revenue || 0, overview?.currency)}
                subtitle="From ticket sales"
                icon={DollarSign}
                color="green"
              />
              <StatCard
                title="Avg Ticket Price"
                value={formatCurrency(overview?.tickets?.avg_price || 0, overview?.currency)}
                subtitle="Per ticket"
                icon={TrendingUp}
                color="purple"
              />
              <StatCard
                title="Event Views"
                value={viewsData?.total_views || 0}
                subtitle={`Last ${timeRange} days`}
                icon={Eye}
                color="amber"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Trend */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle>Revenue Trend</CardTitle>
                  <CardDescription>Daily revenue over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {revenueTrend.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueTrend}>
                          <defs>
                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#666"
                            tickFormatter={(value) => format(new Date(value), 'MMM d')}
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis 
                            stroke="#666"
                            tickFormatter={(value) => {
                              const sym = CURRENCY_CONFIG[overview?.currency]?.symbol || '₦';
                              return `${sym}${(value / 1000).toFixed(0)}k`;
                            }}
                            tick={{ fontSize: 10 }}
                          />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                            formatter={(value) => [formatCurrency(value, overview?.currency), 'Revenue']}
                            labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="revenue" 
                            stroke="#7c3aed" 
                            fillOpacity={1} 
                            fill="url(#colorRevenue)" 
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No revenue data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Views Trend */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle>Views Trend</CardTitle>
                  <CardDescription>Daily event views</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {viewsData?.views_trend?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={viewsData.views_trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#666"
                            tickFormatter={(value) => format(new Date(value), 'MMM d')}
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                            formatter={(value) => [value, 'Views']}
                            labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="views" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No views data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Ticket Sales by Type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle>Sales by Ticket Type</CardTitle>
                  <CardDescription>Distribution of ticket sales</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {ticketSalesByType.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={ticketSalesByType}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="quantity"
                            nameKey="name"
                          >
                            {ticketSalesByType.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No ticket sales data
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Top Performing Events */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle>Top Performing Events</CardTitle>
                  <CardDescription>By ticket sales</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {eventAnalytics.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={eventAnalytics.slice(0, 5)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis type="number" stroke="#666" />
                          <YAxis 
                            type="category" 
                            dataKey="title" 
                            stroke="#666" 
                            width={100}
                            tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                            tick={{ fontSize: 10 }}
                          />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                          />
                          <Bar dataKey="tickets_sold" fill="#7c3aed" radius={[0, 4, 4, 0]} name="Tickets Sold" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No event data
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                title="Total Revenue"
                value={formatCurrency(overview?.tickets?.revenue || 0, overview?.currency)}
                subtitle="All time earnings"
                icon={DollarSign}
                color="green"
              />
              <StatCard
                title="Avg. Ticket Price"
                value={formatCurrency(overview?.tickets?.total_sold > 0 ? (overview?.tickets?.revenue / overview?.tickets?.total_sold) : 0, overview?.currency)}
                subtitle="Per ticket"
                icon={Ticket}
                color="blue"
              />
              <StatCard
                title="Conversion Rate"
                value={`${overview?.engagement?.conversion_rate || 0}%`}
                subtitle="Views to sales"
                icon={TrendingUp}
                color="amber"
              />
            </div>

            {/* Full Width Revenue Chart */}
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <CardTitle>Revenue Over Time</CardTitle>
                <CardDescription>Track your earnings trend</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  {revenueTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={revenueTrend}>
                        <defs>
                          <linearGradient id="colorRevenueMain" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#666"
                          tickFormatter={(value) => format(new Date(value), 'MMM d')}
                        />
                        <YAxis 
                          stroke="#666"
                          tickFormatter={(value) => {
                            const sym = CURRENCY_CONFIG[overview?.currency]?.symbol || '₦';
                            return `${sym}${(value / 1000).toFixed(0)}k`;
                          }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                          formatter={(value) => [formatCurrency(value, overview?.currency), 'Revenue']}
                          labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorRevenueMain)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      No revenue data available for this period
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Revenue by Event */}
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <CardTitle>Revenue by Event</CardTitle>
                <CardDescription>Earnings breakdown for each event</CardDescription>
              </CardHeader>
              <CardContent>
                {eventAnalytics.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No events yet
                  </div>
                ) : (
                  <div className="space-y-4">
                    {eventAnalytics.map((event) => (
                      <div key={event.event_id} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                        <div>
                          <p className="font-medium">{event.title}</p>
                          <p className="text-sm text-muted-foreground">{event.tickets_sold} tickets sold</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-500">{formatCurrency(event.revenue, event.currency)}</p>
                          <p className="text-xs text-muted-foreground">{event.occupancy_rate}% occupancy</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audience Tab */}
          <TabsContent value="audience" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Attendees"
                value={audienceData?.total_attendees || 0}
                subtitle="All tickets purchased"
                icon={Users}
                color="primary"
              />
              <StatCard
                title="Unique Attendees"
                value={audienceData?.unique_attendees || 0}
                subtitle="Individual buyers"
                icon={UserCheck}
                color="blue"
              />
              <StatCard
                title="Repeat Attendees"
                value={audienceData?.repeat_attendees || 0}
                subtitle="Purchased 2+ times"
                icon={Repeat}
                color="green"
              />
              <StatCard
                title="Repeat Rate"
                value={`${audienceData?.repeat_rate || 0}%`}
                subtitle="Customer loyalty"
                icon={TrendingUp}
                color="purple"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Attendees */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle>Top Attendees</CardTitle>
                  <CardDescription>Your most loyal customers</CardDescription>
                </CardHeader>
                <CardContent>
                  {audienceData?.top_attendees?.length > 0 ? (
                    <div className="space-y-4">
                      {audienceData.top_attendees.map((attendee, index) => (
                        <div key={attendee.name || index} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{attendee.name}</p>
                              {attendee.phone && (
                                <p className="text-xs text-muted-foreground">***{attendee.phone}</p>
                              )}
                            </div>
                          </div>
                          <Badge variant="secondary">{attendee.tickets_purchased} tickets</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No attendee data yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Attendance by Category */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle>Attendance by Category</CardTitle>
                  <CardDescription>Which event types attract more attendees</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {audienceData?.attendance_by_category?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={audienceData.attendance_by_category}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="attendees"
                            nameKey="category"
                            label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                          >
                            {audienceData.attendance_by_category.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No category data
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                title="Total Events"
                value={overview?.events?.total || 0}
                icon={Calendar}
                color="primary"
              />
              <StatCard
                title="Published"
                value={overview?.events?.published || 0}
                subtitle="Live events"
                icon={Eye}
                color="green"
              />
              <StatCard
                title="Upcoming"
                value={overview?.events?.upcoming || 0}
                subtitle="Scheduled events"
                icon={Calendar}
                color="blue"
              />
            </div>

            {/* Events Table */}
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <CardTitle>All Events Performance</CardTitle>
                <CardDescription>Detailed metrics for each event</CardDescription>
              </CardHeader>
              <CardContent>
                {eventAnalytics.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No events yet. Create your first event to see analytics.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border/50">
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Event</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Views</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Tickets</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Occupancy</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventAnalytics.map((event) => {
                          const eventViews = viewsData?.views_by_event?.find(v => v.event_id === event.event_id)?.views || 0;
                          return (
                            <tr key={event.event_id} className="border-b border-border/30 hover:bg-muted/30">
                              <td className="py-3 px-4">
                                <p className="font-medium truncate max-w-[200px]">{event.title}</p>
                                <p className="text-xs text-muted-foreground capitalize">{event.category}</p>
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {format(new Date(event.date), 'MMM d, yyyy')}
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={event.is_published ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                                  {event.is_published ? 'Published' : 'Draft'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <span className="text-muted-foreground">{eventViews}</span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <span className="font-medium">{event.tickets_sold}</span>
                                <span className="text-muted-foreground">/{event.total_capacity}</span>
                              </td>
                              <td className="py-3 px-4 text-right font-medium text-green-500">
                                {formatCurrency(event.revenue, event.currency)}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-primary rounded-full transition-all"
                                      style={{ width: `${Math.min(event.occupancy_rate, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-sm">{event.occupancy_rate}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
